
import { Component, Injector } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioButton, MatRadioChange, MatRadioGroup, MatRadioModule } from '@angular/material/radio';
import { MatToolbarModule } from '@angular/material/toolbar';
import { DomSanitizer } from '@angular/platform-browser';
import { DictionaryType } from 'src/app/apiAndObjects/api/dictionaryType.enum';
import { SourcesDictionaryItem } from 'src/app/apiAndObjects/objects/dictionaries/sourcesDictionaryItem';
import { SelectableObservationSummary } from 'src/app/apiAndObjects/objects/selectableObservationSummary';
import { baseLayerDefaultOptions, BaseLayerOption, MapLayer } from 'src/app/bgsLeaflet/public_api';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { ObservationsService } from 'src/app/services/observations.service';
import { Dictionary } from '../../apiAndObjects/public_api';
import { EVMapService } from '../ev-map/ev-map.service';
import { SearchResultsMapLayer } from '../ev-map/mapLayers/searchResultsMapLayer';
import { EVSidebarService } from '../ev-sidebar/ev-sidebar.service';
import { ResultSourceItem } from './resultSourceItem';

@Component({
  selector: 'app-ev-layers',
  templateUrl: './ev-layers.component.html',
  styleUrls: ['./ev-layers.component.scss'],
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioButton,
    MatRadioModule,
    MatRadioGroup,
    FormsModule
],
})
export class EvLayersComponent {
  public mapLayers = new Array<MapLayer>();
  public baseLayerOptions = BASE_LAYER_OPTIONS;
  public currentBaseLayerOption: BaseLayerOption;

  private results = new Array<SelectableObservationSummary>();
  public resultSourceArray = new Array<ResultSourceItem>();

  constructor(
    private readonly mapService: EVMapService,
    private readonly sidebarService: EVSidebarService,
    private readonly injector: Injector,
    public observationsService: ObservationsService,
    private readonly sanitized: DomSanitizer,
    public dictionaryService: DictionaryService,
  ) {
    dictionaryService.getDictionary(DictionaryType.SOURCES).then((dict: Dictionary) => {
      this.resultSourceArray = dict.getItems().map((item: SourcesDictionaryItem) => {
        return new ResultSourceItem(item);
      });
      this.updateResultSourceItemCounts();
    });

    mapService.watchMapLayers().subscribe((mapLayers: Array<MapLayer>) => {
      this.mapLayers = mapLayers.slice();
    });

    observationsService.watchSearchResults().subscribe((results: Array<SelectableObservationSummary>) => {
      this.results = results;
      this.updateResultSourceItemCounts();
      this.refreshGeoJsonLayer();
    });

    this.refreshGeoJsonLayer();
    // console.log('layers maplayers: ', this.mapLayers);

    // select Base Layer(s)
    this.currentBaseLayerOption = this.baseLayerOptions.find((layer) => layer.name === 'Streets');
    this.baseLayerChange();
  }

  public closeSidePanel() {
    this.sidebarService.clearSidebarMode();
  }

  public changeMapLayer(event: MatCheckboxChange, layer: MapLayer): void {
    layer.hidden.set(!event.checked);
  }

  public changeResultLayer(event: MatCheckboxChange, resultSourceItem: ResultSourceItem): void {
    resultSourceItem.selected = event.checked;
    this.refreshGeoJsonLayer();
  }

  private updateResultSourceItemCounts(): void {
    const quickAccessObj = {};
    this.resultSourceArray.forEach((resultSourceItem: ResultSourceItem) => {
      // reset counts
      resultSourceItem.totalCount = 0;
      resultSourceItem.filterCount = 0;
      quickAccessObj[resultSourceItem.id] = resultSourceItem;
    });

    this.results.forEach((item: SelectableObservationSummary) => {
      const resultSourceItem = quickAccessObj[item.source.id];
      if (null != resultSourceItem) {
        resultSourceItem.totalCount++;
        if (item.isSelected) {
          resultSourceItem.filterCount++;
        }
      }
    });
  }

  private refreshGeoJsonLayer(): void {
    const quickAccessObj = {};
    this.resultSourceArray.forEach((resultSourceItem: ResultSourceItem) => {
      quickAccessObj[resultSourceItem.id] = resultSourceItem.selected;
    });
    const results = this.results.filter((item: SelectableObservationSummary) => {
      return quickAccessObj[item.source.id] === true;
    });
    this.mapService.addMapLayer(new SearchResultsMapLayer(results, this.injector, this.sanitized));
  }

  public baseLayerChange(event?: MatRadioChange): void {
    const selectedOption: BaseLayerOption = null == event ? this.currentBaseLayerOption : event.value;
    this.mapService.setBaseLayers(selectedOption.getLayers());
  }
}

// default options here
// tslint:disable-next-line: max-line-length
// https://kwvmxgit.ad.nerc.ac.uk/Angular-modules/bgs-leaflet/-/blob/master/projects/bgs-leaflet/src/lib/components/map/controls/baseLayerControl/baseLayerDefaultOptions.ts
const BASE_LAYER_OPTIONS = baseLayerDefaultOptions.filter((option: BaseLayerOption) => {
  return (
    null !=
    [
      'None',
      'Streets',
      'Topographic',
      'National Geographic',
      'Oceans',
      'Imagery',
      'Imagery Clarity',
      'Imagery Firefly',
      'Shaded Relief',
      'Terrain',
      'Physical',
    ].find((optionName: string) => optionName.toLowerCase() === option.name.toLowerCase())
  );
});
